import { useCallback } from "react";
import { tracking } from "@babackapp/transactional-emails/lib";
import { useTranslation } from "react-i18next";
import { useCreateTemplate } from "./use-create-template";
import type { TemplateType } from "../types";
import type { EmailTemplateContentDto } from "@babackapp/private-api-client/types";

export type TrackingStatusKey = keyof typeof tracking.defaultEmailTemplates;

const defaultWebTemplate: Omit<EmailTemplateContentDto, "defaultLocale"> = {
  beforeBlocks: [
    {
      id: "5641c370-5792-4a39-8baf-d878fa78d6a8",
      type: "logo",
      alignment: "center",
      properties: {},
      content: null,
    },
  ],
  insideBlocks: [
    {
      id: "5641c370-5792-4a39-8baf-d878fa78d6a2",
      type: "last-status",
      alignment: "center",
      properties: {},
      content: null,
    },
    {
      id: "5641c371-5792-4a39-8baf-d878fa78d6a2",
      type: "estimated-delivery-date",
      alignment: "center",
      properties: {},
      content: null,
    },
    {
      id: "580af869-4d91-4fd0-a2f2-1a3e10206491",
      type: "stepper",
      alignment: "center",
      properties: {},
      content: null,
    },
    {
      id: "6f989aa2-4a5e-4fa6-b548-ca505a5dbebe",
      type: "pickup-point",
      alignment: "center",
      properties: {},
      content: null,
    },
    {
      id: "dd0608b9-7a98-4728-b7d9-9b203288db3b",
      type: "delivery-address",
      alignment: "center",
      properties: {},
      content: null,
    },
    {
      id: "89bd0855-a562-41d3-a0a3-82501519fa54",
      type: "package-content",
      alignment: "center",
      properties: {},
      content: null,
    },
    {
      id: "44bd599e-29b6-4df4-97f0-f08fd2fef447",
      type: "tracking-history",
      alignment: "center",
      properties: {},
      content: null,
    },
  ],
  afterBlocks: [
    {
      id: "baf77ab8-d0fb-429b-902f-93537ba6345f",
      type: "wallet",
      alignment: "center",
      properties: {},
      content: null,
    },
  ],
};

export function useCreateTemplateFromModel() {
  const { t } = useTranslation("trackoffice");
  const { createTemplate, isLoading } = useCreateTemplate();

  const templateModels = Object.entries(tracking.defaultEmailTemplates) as [
    TrackingStatusKey,
    (typeof tracking.defaultEmailTemplates)[TrackingStatusKey],
  ][];

  const createFromModel = useCallback(
    async (modelKey: TrackingStatusKey, type: TemplateType = "email") => {
      const model = tracking.defaultEmailTemplates[modelKey];

      return createTemplate({
        type,
        title: model.title,
        content: model.content as EmailTemplateContentDto,
        subject: model.subject as Record<string, unknown>,
        preview: model.preview as Record<string, unknown>,
        category: model.category ?? undefined,
      });
    },
    [createTemplate],
  );

  const createWebTemplate = useCallback(async () => {
    const title = t(
      "templatesEditor.defaultWebTemplate.title",
      "Web tracking page",
    );

    return createTemplate({
      type: "web",
      title,
      content: defaultWebTemplate as EmailTemplateContentDto,
    });
  }, [createTemplate, t]);

  return {
    createFromModel,
    createWebTemplate,
    templateModels,
    isLoading,
  };
}
